/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { cn } from '@/lib/utils';
import type { Auction, Drop } from '../../../api/areas/market';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Section, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { FactRows, GateList, HeroCard, SplitPane, Stepper, type StepState } from '../../../components/flow';
import { DomainLink } from '../../../components/Ids';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState, Loading } from '../../../components/States';
import { RefreshButton } from '../../registry/shared';
import { AuctionLinks, useAuctionRows, useBackorderRows } from '../module';
import { CustomerRef, money } from '../shared';

type Stage = 'won' | 'paid' | 'order' | 'assigned';
const STAGES: Stage[] = ['won', 'order', 'paid', 'assigned'];

/** One name on its way to its new holder, whether it came from an auction or a drop catch. */
interface Handover {
  key: string;
  domain: string;
  source: 'auction' | 'backorder';
  customerId: string | null;
  customerName: string | null;
  amount: string | null;
  currency: string;
  orderId: string | null;
  domainId: string | null;
  captured: boolean;
  /** How far it got (the last stage reached). */
  stage: Stage;
  failed: boolean;
  link: string;
}

function fromAuction(a: Auction): Handover {
  const captured = Boolean(a.capture_entry_id) || a.status === 'SETTLED';
  const stage: Stage = a.domain_id ? 'assigned' : captured ? 'paid' : a.order_id ? 'order' : 'won';
  return {
    key: `a-${a.id}`,
    domain: a.domain_name,
    source: 'auction',
    customerId: a.winner_customer_id,
    customerName: a.winner_name,
    amount: a.final_price,
    currency: a.currency,
    orderId: a.order_id,
    domainId: a.domain_id,
    captured,
    stage,
    failed: a.status === 'FAILED',
    link: `/auctions/${encodeURIComponent(a.id)}`,
  };
}

function fromDrop(d: Drop): Handover | null {
  const x = d.attempt;
  if (!x) return null;
  const captured = Boolean(x.capture_entry_id);
  const stage: Stage = x.domain_id ? 'assigned' : captured ? 'paid' : x.order_id ? 'order' : 'won';
  return {
    key: `d-${d.id}`,
    domain: d.domain_name,
    source: 'backorder',
    customerId: x.customer_id,
    customerName: x.customer_name,
    amount: x.amount,
    currency: x.currency,
    orderId: x.order_id,
    domainId: x.domain_id,
    captured,
    stage,
    failed: false,
    link: `/drops/${encodeURIComponent(d.id)}`,
  };
}

const STAGE_TONE: Record<Stage, Tone> = { won: 'pending', paid: 'info', order: 'info', assigned: 'ok' };

function HandoverDetail({ h }: { h: Handover }) {
  const { t } = useTranslation('market');
  const reached = STAGES.indexOf(h.stage);
  const state = (i: number): StepState => (i <= reached ? 'done' : i === reached + 1 ? (h.failed ? 'blocked' : 'current') : 'todo');
  return (
    <div className="space-y-5">
      <HeroCard kicker={t(`au.handover.source.${h.source}`)} title={h.domain} badge={<Badge tone={h.failed ? 'crit' : STAGE_TONE[h.stage]}>{h.failed ? t('au.handover.failed') : t(`au.handover.stage.${h.stage}`)}</Badge>}>
        <FactRows
          rows={[
            [t('au.handover.recipient'), <CustomerRef key="c" id={h.customerId} name={h.customerName} />],
            [t('au.handover.amount'), h.amount ? money(h.amount, h.currency) : '—'],
            [
              t('auction.order'),
              h.orderId ? (
                <Link key="o" to={`/orders/${encodeURIComponent(h.orderId)}`} className="text-primary hover:underline">
                  {t('auction.openOrder')}
                </Link>
              ) : (
                '—'
              ),
            ],
            [t('auction.domain'), h.domainId ? <DomainLink key="d" keyOrName={h.domain} /> : '—'],
          ]}
        />
      </HeroCard>
      <Section title={t('au.handover.flow')}>
        <Stepper className="md:flex-col md:[&>li[aria-hidden]]:hidden" label={t('au.handover.flow')} steps={STAGES.map((s, i) => ({ id: s, label: t(`au.handover.stage.${s}`), state: state(i) }))} />
      </Section>
      <Section title={t('au.handover.gatesTitle')}>
        <GateList
          gates={[
            { id: 'paid', title: t('au.handover.gate.paid'), hint: t('au.handover.gate.paidHint'), ok: h.captured },
            { id: 'dossier', title: t('au.handover.gate.dossier'), hint: t('au.handover.gate.dossierHint'), ok: h.domainId ? true : null },
            { id: 'code', title: t('au.handover.gate.code'), hint: t('au.handover.gate.codeHint'), ok: null },
          ]}
        />
        <Link to={h.link} className="mt-4 inline-block text-sm font-medium text-primary hover:underline">
          {h.source === 'auction' ? t('au.settlement.openSession') : t('au.handover.openDrop')}
        </Link>
      </Section>
    </div>
  );
}

/** `/auctions?tab=handover`, reference 69: names won at auction or caught for a backorder, until they are in the new holder's account. */
export function AuctionHandoverPage() {
  const { t } = useTranslation('market');
  const [params, setParams] = useSearchParams();
  const auctions = useAuctionRows();
  const { drops, reload } = useBackorderRows();
  const refresh = () => {
    auctions.reload();
    reload();
  };
  const header = <PageHeader title={t('au.handover.title')} subtitle={t('au.handover.subtitle')} actions={<RefreshButton onClick={refresh} />} />;
  if (auctions.status !== 'success' || drops.status !== 'success') {
    const error = auctions.status === 'error' ? auctions.error : drops.status === 'error' ? drops.error : null;
    return (
      <>
        {header}
        <AuctionLinks here="handover" />
        {error ? <DataError error={error} onRetry={refresh} title={t('auctions.loadError')} /> : <Loading rows={5} />}
      </>
    );
  }
  const rows: Handover[] = [
    ...auctions.data.items.filter((a) => a.winner_bid_id && (a.status === 'CLOSED' || a.status === 'SETTLING' || a.status === 'SETTLED' || a.status === 'FAILED')).map(fromAuction),
    ...drops.data.items.filter((d) => d.attempt && (d.attempt.status === 'REGISTERING' || d.attempt.status === 'CAUGHT')).flatMap((d) => fromDrop(d) ?? []),
  ];
  const picked = rows.find((r) => r.key === params.get('item')) ?? rows[0];
  const pick = (h: Handover) => {
    const p = new URLSearchParams(params);
    p.set('item', h.key);
    setParams(p, { replace: true });
  };
  const columns: Column<Handover>[] = [
    {
      key: 'domain',
      header: t('backorders.domain'),
      cell: (h) => (
        <button type="button" onClick={() => pick(h)} className="text-left font-semibold text-primary hover:underline">
          {h.domain}
        </button>
      ),
    },
    { key: 'source', header: t('au.handover.from'), cell: (h) => t(`au.handover.source.${h.source}`) },
    { key: 'recipient', header: t('au.handover.recipient'), className: 'max-w-44 whitespace-normal', cell: (h) => <CustomerRef id={h.customerId} name={h.customerName} /> },
    { key: 'stage', header: t('au.handover.stageCol'), cell: (h) => <Badge tone={h.failed ? 'crit' : STAGE_TONE[h.stage]}>{h.failed ? t('au.handover.failed') : t(`au.handover.stage.${h.stage}`)}</Badge> },
  ];
  return (
    <>
      {header}
      <AuctionLinks here="handover" />
      <div className="space-y-5">
        <SplitPane
          list={
            <Section flush title={t('au.handover.list')} description={t('au.handover.listHint')}>
              <DataTable columns={columns} rows={rows} rowKey={(h) => h.key} onRowClick={pick} rowClassName={(h) => cn(h.key === picked?.key && 'bg-accent/60')} empty={<EmptyState title={t('au.handover.none')} />} />
            </Section>
          }
          detail={picked ? <HandoverDetail h={picked} /> : null}
        />
        <Callout tone="info" title={t('au.handover.noteTitle')}>
          {t('au.handover.note')}
        </Callout>
      </div>
    </>
  );
}
